import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PlatformKpi } from "@/components/PlatformKpi";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Building2, Users, UserCog, ClipboardCheck, MapPinned, CalendarClock, Bot, AlertTriangle, Smartphone, Activity, ArrowUpRight, TrendingUp, Loader2 } from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, AreaChart, Area,
  PieChart, Pie, Cell, BarChart, Bar, CartesianGrid, Legend,
} from "recharts";
import { toast } from "sonner";
import { useAuth } from "@/auth/AuthContext";
import {
  DashboardOverview,
  dashboardApi,
  tierToLabel,
} from "@/lib/platform-api";
import { ApiError } from "@/lib/api";

const PLAN_FILLS: Record<string, string> = {
  Basic: "hsl(var(--muted-foreground))",
  Pro: "hsl(var(--info))",
  Premium: "hsl(var(--primary))",
  Enterprise: "hsl(var(--accent))",
};

export default function PlatformDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState<DashboardOverview | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    dashboardApi
      .overview()
      .then((res) => { if (!cancelled) setData(res); })
      .catch((e) => {
        if (cancelled) return;
        const msg = e instanceof ApiError ? e.message : "Failed to load dashboard";
        toast.error("Failed to load dashboard", { description: msg });
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const planDistributionWithFill = useMemo(() => {
    if (!data) return [];
    return data.planDistribution.map((d) => ({
      ...d,
      fill: PLAN_FILLS[d.label] ?? "hsl(var(--primary))",
    }));
  }, [data]);

  const tenantGrowthDelta = useMemo(() => {
    if (!data || data.tenantGrowth.length < 2) return null;
    const first = data.tenantGrowth[0].tenants;
    const last = data.tenantGrowth[data.tenantGrowth.length - 1].tenants;
    if (first === 0) return null;
    const pct = Math.round(((last - first) / first) * 100);
    return { pct, direction: pct >= 0 ? "up" : "down" as const };
  }, [data]);

  const firstName = user?.name?.split(" ")[0] ?? "Admin";

  if (loading || !data) {
    return (
      <div className="flex flex-col items-center justify-center py-32 text-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        <p className="mt-3 text-sm text-muted-foreground">Loading platform overview...</p>
      </div>
    );
  }

  const k = data.kpis;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-2xl font-bold tracking-tight">Welcome back, {firstName}</h2>
          <p className="text-sm text-muted-foreground">
            Strategic overview across {k.totalTenants} tenants and {k.totalEmployees.toLocaleString()} employees.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={() => navigate("/platform/tenants")}>
            <Building2 className="mr-1.5 h-4 w-4" /> Manage Tenants
          </Button>
          <Button size="sm" className="bg-gradient-to-r from-accent to-primary hover:opacity-90" onClick={() => navigate("/platform/tenants")}>
            <TrendingUp className="mr-1.5 h-4 w-4" /> View Analytics
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-5">
        <PlatformKpi label="Total Tenants" value={k.totalTenants} icon={Building2} accent="primary" />
        <PlatformKpi label="Active Users" value={k.activeUsers.toLocaleString()} icon={Users} accent="accent" />
        <PlatformKpi label="Total Employees" value={k.totalEmployees.toLocaleString()} icon={UserCog} accent="primary" />
        <PlatformKpi label="Mobile Active" value={k.mobileActiveUsers.toLocaleString()} icon={Smartphone} accent="success" />
        <PlatformKpi label="Attendance Today" value={k.attendanceToday.toLocaleString()} icon={Activity} accent="success" />
        <PlatformKpi label="Pending Requests" value={k.pendingRequests} icon={ClipboardCheck} accent="warning" />
        <PlatformKpi label="Total Sites" value={k.totalSites} hint={`${k.activeSites} active`} icon={MapPinned} accent="primary" />
        <PlatformKpi label="Renewals Due" value={k.renewalsDueThisMonth} hint="this month" icon={CalendarClock} accent="warning" />
        <PlatformKpi label="AI Interactions" value={k.aiInteractionsToday.toLocaleString()} hint="today" icon={Bot} accent="accent" />
        <PlatformKpi label="Platform Alerts" value={k.platformAlerts} hint="last 24h" icon={AlertTriangle} accent="destructive" />
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-base">Tenant Growth</CardTitle>
                <CardDescription>Tenants onboarded over the last 6 months</CardDescription>
              </div>
              {tenantGrowthDelta && (
                <Badge variant="outline" className={
                  tenantGrowthDelta.direction === "up"
                    ? "border-success/30 bg-success/10 text-success"
                    : "border-destructive/30 bg-destructive/10 text-destructive"
                }>
                  {tenantGrowthDelta.pct >= 0 ? "+" : ""}{tenantGrowthDelta.pct}% vs 6mo
                </Badge>
              )}
            </div>
          </CardHeader>
          <CardContent className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data.tenantGrowth} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="grow" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="hsl(var(--accent))" stopOpacity={0.4} />
                    <stop offset="95%" stopColor="hsl(var(--accent))" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" fontSize={11} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} allowDecimals={false} />
                <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
                <Area type="monotone" dataKey="tenants" stroke="hsl(var(--accent))" strokeWidth={2.5} fill="url(#grow)" />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Plan Distribution</CardTitle>
            <CardDescription>Tenants per subscription tier</CardDescription>
          </CardHeader>
          <CardContent className="h-72">
            {planDistributionWithFill.every(p => p.value === 0) ? (
              <EmptyState label="No tenants yet" />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={planDistributionWithFill} dataKey="value" nameKey="label" innerRadius={50} outerRadius={85} paddingAngle={2}>
                    {planDistributionWithFill.map((d, i) => <Cell key={i} fill={d.fill} />)}
                  </Pie>
                  <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* --- Merged in from the retired Usage & Analytics page (FEAT-2026-006) --- */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Daily Active Users</CardTitle>
            <CardDescription>Employees with attendance activity · last 30 days</CardDescription>
          </CardHeader>
          <CardContent className="h-64">
            {data.dailyActiveUsers.every(d => d.users === 0) ? (
              <EmptyState label="No mobile activity yet" />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={data.dailyActiveUsers} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="gDau" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.4} />
                      <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                  <XAxis dataKey="day" stroke="hsl(var(--muted-foreground))" fontSize={11} interval="preserveStartEnd" />
                  <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} allowDecimals={false} />
                  <Tooltip
                    labelFormatter={(_l, p) => p?.[0]?.payload?.date ?? ''}
                    contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }}
                  />
                  <Area type="monotone" dataKey="users" stroke="hsl(var(--primary))" strokeWidth={2.5} fill="url(#gDau)" />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Geofence Alerts</CardTitle>
            <CardDescription>Check-ins outside the site boundary · last 30 days</CardDescription>
          </CardHeader>
          <CardContent className="h-64">
            {data.geofenceAlerts.every(d => d.alerts === 0) ? (
              <EmptyState label="No geofence alerts" />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={data.geofenceAlerts} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                  <XAxis dataKey="day" stroke="hsl(var(--muted-foreground))" fontSize={11} interval="preserveStartEnd" />
                  <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} allowDecimals={false} />
                  <Tooltip
                    labelFormatter={(_l, p) => p?.[0]?.payload?.date ?? ''}
                    contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }}
                  />
                  <Area type="monotone" dataKey="alerts" stroke="hsl(0 72% 51%)" strokeWidth={2} fill="hsl(0 72% 51% / 0.12)" />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Request Volume</CardTitle>
            <CardDescription>Requests submitted per day · last 30 days</CardDescription>
          </CardHeader>
          <CardContent className="h-64">
            {data.requestVolume.every(d => d.requests === 0) ? (
              <EmptyState label="No requests submitted yet" />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={data.requestVolume} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                  <XAxis dataKey="day" stroke="hsl(var(--muted-foreground))" fontSize={11} interval="preserveStartEnd" />
                  <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} allowDecimals={false} />
                  <Tooltip
                    labelFormatter={(_l, p) => p?.[0]?.payload?.date ?? ''}
                    contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }}
                  />
                  <Line type="monotone" dataKey="requests" stroke="hsl(var(--accent))" strokeWidth={2.5} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Attendance Volume — Platform-wide</CardTitle>
            <CardDescription>Daily attendance records across all tenants (last 7 days)</CardDescription>
          </CardHeader>
          <CardContent className="h-64">
            {data.attendanceVolume.every(p => p.records === 0) ? (
              <EmptyState label="No attendance records yet" />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={data.attendanceVolume} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="day" stroke="hsl(var(--muted-foreground))" fontSize={11} />
                  <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} allowDecimals={false} />
                  <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
                  <Line type="monotone" dataKey="records" stroke="hsl(var(--primary))" strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Requests by Status</CardTitle>
            <CardDescription>Across all tenants</CardDescription>
          </CardHeader>
          <CardContent className="h-64">
            {data.requestsByStatus.every(r => r.count === 0) ? (
              <EmptyState label="No correction requests yet" />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.requestsByStatus} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="label" stroke="hsl(var(--muted-foreground))" fontSize={11} />
                  <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} allowDecimals={false} />
                  <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
                  <Bar dataKey="count" fill="hsl(var(--primary))" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-base">Top Active Tenants</CardTitle>
                <CardDescription>Ranked by attendance volume today</CardDescription>
              </div>
              <Button variant="ghost" size="sm" onClick={() => navigate("/platform/tenants")}>
                View all <ArrowUpRight className="ml-1 h-3.5 w-3.5" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-2">
            {data.topTenants.length === 0 && (
              <p className="py-6 text-center text-sm text-muted-foreground">No attendance recorded today.</p>
            )}
            {data.topTenants.map((t, i) => (
              <div
                key={t.id}
                className="flex items-center gap-3 rounded-lg border bg-card p-3 hover:bg-secondary/40 transition-colors cursor-pointer"
                onClick={() => navigate(`/platform/tenants/${t.id}`)}
              >
                <div className="flex h-8 w-8 items-center justify-center rounded-md bg-gradient-to-br from-primary to-accent text-xs font-semibold text-white">{i + 1}</div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <div className="truncate text-sm font-medium">{t.name}</div>
                    <Badge variant="outline" className="text-[10px]">{tierToLabel(t.plan)}</Badge>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {t.industry ?? "—"} · {t.activeUsers.toLocaleString()} active users
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-display text-sm font-semibold">{t.attendanceToday.toLocaleString()}</div>
                  <div className="text-[10px] text-muted-foreground">records today</div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Recent Platform Alerts</CardTitle>
            <CardDescription>Cross-tenant signals</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {data.recentAlerts.length === 0 && (
              <p className="py-6 text-center text-sm text-muted-foreground">All quiet. No alerts.</p>
            )}
            {data.recentAlerts.map((a) => (
              <div key={a.id} className="flex items-start gap-2.5 rounded-lg border p-2.5">
                <Badge variant="outline" className={
                  a.severity === "CRITICAL" ? "border-destructive/40 bg-destructive/10 text-destructive" :
                  a.severity === "WARNING" ? "border-warning/40 bg-warning/10 text-warning" :
                  "border-info/40 bg-info/10 text-info"
                }>{a.severity.toLowerCase()}</Badge>
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-medium">{a.message}</div>
                  <div className="text-[10px] text-muted-foreground">
                    {a.tenantName ? `${a.tenantName} · ` : ""}{relativeTime(a.createdAt)}
                  </div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function EmptyState({ label }: { label: string }) {
  return (
    <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
      {label}
    </div>
  );
}

function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  const diffMs = Date.now() - then;
  const min = Math.round(diffMs / 60000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.round(hr / 24);
  if (day < 30) return `${day}d ago`;
  return new Date(iso).toLocaleDateString();
}
